"use client";

import mapboxgl, { type MarkerOptions } from "mapbox-gl";
import type React from "react";
import { useEffect, useRef } from "react";
import { useMap } from "../context";

type MarkerProps = {
  longitude: number;
  latitude: number;
  onHover?: (args: {
    isHovered: boolean;
    position: { longitude: number; latitude: number };
    marker: mapboxgl.Marker;
  }) => void;
  onClick?: (args: {
    position: { longitude: number; latitude: number };
    marker: mapboxgl.Marker;
  }) => void;
  children?: React.ReactNode;
} & MarkerOptions;

export function Marker({
  children,
  latitude,
  longitude,
  onHover,
  onClick,
  ...props
}: MarkerProps) {
  const { map } = useMap();
  const markerRef = useRef<HTMLDivElement | null>(null);
  const markerInstanceRef = useRef<mapboxgl.Marker | null>(null);

  // biome-ignore lint/correctness/useExhaustiveDependencies: props spread changes reference every render
  useEffect(() => {
    const markerEl = markerRef.current;
    if (!(map && markerEl)) {
      return;
    }

    const handleMouseEnter = () => {
      if (onHover && markerInstanceRef.current) {
        onHover({
          isHovered: true,
          position: { longitude, latitude },
          marker: markerInstanceRef.current,
        });
      }
    };

    const handleMouseLeave = () => {
      if (onHover && markerInstanceRef.current) {
        onHover({
          isHovered: false,
          position: { longitude, latitude },
          marker: markerInstanceRef.current,
        });
      }
    };

    const handleClick = () => {
      if (onClick && markerInstanceRef.current) {
        onClick({
          position: { longitude, latitude },
          marker: markerInstanceRef.current,
        });
      }
    };

    markerEl.addEventListener("mouseenter", handleMouseEnter);
    markerEl.addEventListener("mouseleave", handleMouseLeave);
    markerEl.addEventListener("click", handleClick);

    markerInstanceRef.current = new mapboxgl.Marker({
      element: markerEl,
      ...props,
    })
      .setLngLat([longitude, latitude])
      .addTo(map);

    return () => {
      markerInstanceRef.current?.remove();
      markerInstanceRef.current = null;
      markerEl.removeEventListener("mouseenter", handleMouseEnter);
      markerEl.removeEventListener("mouseleave", handleMouseLeave);
      markerEl.removeEventListener("click", handleClick);
    };
  }, [map, longitude, latitude, onHover, onClick]);

  return (
    <div>
      <div ref={markerRef}>{children}</div>
    </div>
  );
}
